import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, Loader2, Check, ChevronRight, ChevronLeft, Building2, MapPin, ImageOff } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STEPS = ["Provinsi", "Kabupaten/Kota", "Kecamatan", "Desa/Kelurahan", "Konfirmasi"];

export default function Setup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);

  const [provinces, setProvinces] = useState([]);
  const [regencies, setRegencies] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [villages, setVillages] = useState([]);

  const [sel, setSel] = useState({
    province: null, regency: null, district: null, village: null,
  });
  const [logo, setLogo] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [postalLoading, setPostalLoading] = useState(false);
  const [extra, setExtra] = useState({ village_head_name: "", address: "", postal_code: "", phone: "", email: "" });

  // Redirect if already set up
  useEffect(() => {
    api.get("/village-profile").then((res) => {
      if (res.data) navigate("/");
    });
  }, [navigate]);

  useEffect(() => {
    setLoadingList(true);
    api.get("/regions/provinces").then((r) => setProvinces(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoadingList(false));
  }, []);

  const loadRegencies = async (code) => {
    setLoadingList(true);
    try {
      const r = await api.get(`/regions/regencies/${code}`);
      setRegencies(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoadingList(false); }
  };
  const loadDistricts = async (code) => {
    setLoadingList(true);
    try {
      const r = await api.get(`/regions/districts/${code}`);
      setDistricts(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoadingList(false); }
  };
  const loadVillages = async (code) => {
    setLoadingList(true);
    try {
      const r = await api.get(`/regions/villages/${code}`);
      setVillages(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoadingList(false); }
  };

  const fetchLogo = async (regencyName) => {
    setLogoLoading(true);
    setLogo(null);
    try {
      const r = await api.get(`/regions/logo`, { params: { name: regencyName, province: sel.province?.name || "" } });
      setLogo(r.data.logo_url);
      if (r.data.logo_url) toast.success("Logo kabupaten berhasil dimuat");
      else toast.message("Logo tidak ditemukan, dapat diatur manual nanti");
    } catch { setLogo(null); } finally { setLogoLoading(false); }
  };

  const fetchPostal = async (village) => {
    setPostalLoading(true);
    try {
      const r = await api.get(`/regions/postal-code`, {
        params: { village: village.name, district: sel.district?.name || "", regency: sel.regency?.name || "" },
      });
      if (r.data.postal_code) {
        setExtra((x) => ({ ...x, postal_code: r.data.postal_code }));
        toast.success(`Kode pos otomatis: ${r.data.postal_code}`);
      } else {
        toast.message("Kode pos tidak ditemukan, silakan isi manual");
      }
    } catch { /* ignore */ } finally { setPostalLoading(false); }
  };

  const pick = {
    province: (code) => {
      const p = provinces.find((x) => x.code === code);
      setSel({ province: p, regency: null, district: null, village: null });
      setRegencies([]); setDistricts([]); setVillages([]); setLogo(null);
      loadRegencies(code);
    },
    regency: (code) => {
      const r = regencies.find((x) => x.code === code);
      setSel((s) => ({ ...s, regency: r, district: null, village: null }));
      setDistricts([]); setVillages([]);
      loadDistricts(code);
      fetchLogo(r.name);
    },
    district: (code) => {
      const d = districts.find((x) => x.code === code);
      setSel((s) => ({ ...s, district: d, village: null }));
      setVillages([]);
      loadVillages(code);
    },
    village: (code) => {
      const v = villages.find((x) => x.code === code);
      setSel((s) => ({ ...s, village: v }));
      fetchPostal(v);
    },
  };

  const canNext =
    (step === 0 && sel.province) ||
    (step === 1 && sel.regency) ||
    (step === 2 && sel.district) ||
    (step === 3 && sel.village) ||
    step === 4;

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/village-profile", {
        province_code: sel.province.code, province_name: sel.province.name,
        regency_code: sel.regency.code, regency_name: sel.regency.name,
        district_code: sel.district.code, district_name: sel.district.name,
        village_code: sel.village.code, village_name: sel.village.name,
        regency_logo_url: logo,
        ...extra,
      });
      toast.success("Profil desa berhasil disimpan!");
      navigate("/");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-11 w-11 rounded-xl bg-[#064E3B] grid place-items-center">
            <Leaf size={24} className="text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-[#064E3B]">Inisialisasi Wilayah Desa</h1>
            <p className="text-sm text-slate-500">Pilih wilayah dari Provinsi hingga Desa/Kelurahan</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-center mb-8 overflow-x-auto custom-scroll pb-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center shrink-0">
              <div className="flex items-center gap-2" data-testid={`step-indicator-${i}`}>
                <div
                  className={`h-9 w-9 rounded-full grid place-items-center text-sm font-semibold transition-colors ${
                    i < step ? "bg-[#059669] text-white" : i === step ? "bg-[#064E3B] text-white" : "bg-slate-200 text-slate-500"
                  }`}
                >
                  {i < step ? <Check size={16} /> : i + 1}
                </div>
                <span className={`text-sm font-medium ${i === step ? "text-[#064E3B]" : "text-slate-400"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="w-8 h-px bg-slate-200 mx-2" />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 min-h-[320px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              {step === 0 && (
                <StepSelect
                  label="Pilih Provinsi" testid="setup-province-select" loading={loadingList}
                  options={provinces} value={sel.province?.code} onChange={pick.province}
                  placeholder="-- Pilih Provinsi --"
                />
              )}
              {step === 1 && (
                <div className="space-y-6">
                  <StepSelect
                    label="Pilih Kabupaten/Kota" testid="setup-regency-select" loading={loadingList}
                    options={regencies} value={sel.regency?.code} onChange={pick.regency}
                    placeholder="-- Pilih Kabupaten/Kota --"
                  />
                  <LogoPreview logo={logo} loading={logoLoading} name={sel.regency?.name} />
                </div>
              )}
              {step === 2 && (
                <StepSelect
                  label="Pilih Kecamatan" testid="setup-district-select" loading={loadingList}
                  options={districts} value={sel.district?.code} onChange={pick.district}
                  placeholder="-- Pilih Kecamatan --"
                />
              )}
              {step === 3 && (
                <StepSelect
                  label="Pilih Desa/Kelurahan" testid="setup-village-select" loading={loadingList}
                  options={villages} value={sel.village?.code} onChange={pick.village}
                  placeholder="-- Pilih Desa/Kelurahan --"
                />
              )}
              {step === 4 && (
                <div className="space-y-6" data-testid="setup-confirm-step">
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                    {logo ? (
                      <img src={logo} alt="Logo" className="h-16 w-16 object-contain" />
                    ) : (
                      <div className="h-16 w-16 rounded-lg bg-emerald-100 grid place-items-center"><Building2 className="text-[#064E3B]" /></div>
                    )}
                    <div>
                      <div className="font-heading text-lg font-bold text-[#064E3B]">Desa {sel.village?.name}</div>
                      <div className="text-sm text-slate-600 flex items-center gap-1 mt-1"><MapPin size={14} /> Kec. {sel.district?.name}, {sel.regency?.name}</div>
                      <div className="text-sm text-slate-500">{sel.province?.name}</div>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Nama Kepala Desa" value={extra.village_head_name} onChange={(v) => setExtra({ ...extra, village_head_name: v })} testid="setup-head-input" />
                    <Field label={`Kode Pos${postalLoading ? " (memuat otomatis...)" : " (otomatis)"}`} value={extra.postal_code} onChange={(v) => setExtra({ ...extra, postal_code: v })} testid="setup-postal-input" />
                    <Field label="Telepon" value={extra.phone} onChange={(v) => setExtra({ ...extra, phone: v })} testid="setup-phone-input" />
                    <Field label="Email Desa" value={extra.email} onChange={(v) => setExtra({ ...extra, email: v })} testid="setup-email-input" />
                    <div className="sm:col-span-2">
                      <Field label="Alamat Kantor Desa" value={extra.address} onChange={(v) => setExtra({ ...extra, address: v })} testid="setup-address-input" />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex justify-between mt-6">
          <Button
            variant="outline" disabled={step === 0}
            onClick={() => setStep((s) => s - 1)} data-testid="setup-back-button"
            className="h-11 rounded-xl"
          >
            <ChevronLeft size={18} className="mr-1" /> Kembali
          </Button>
          {step < 4 ? (
            <Button
              disabled={!canNext} onClick={() => setStep((s) => s + 1)} data-testid="setup-next-button"
              className="h-11 rounded-xl bg-[#064E3B] hover:bg-[#043E2F]"
            >
              Lanjut <ChevronRight size={18} className="ml-1" />
            </Button>
          ) : (
            <Button
              disabled={saving} onClick={save} data-testid="setup-finish-button"
              className="h-11 rounded-xl bg-[#D97706] hover:bg-[#B45309]"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Check size={18} className="mr-2" />} Selesaikan Setup
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepSelect({ label, testid, loading, options, value, onChange, placeholder }) {
  return (
    <div className="space-y-3">
      <Label className="text-base">{label}</Label>
      <Select value={value || ""} onValueChange={onChange} disabled={loading}>
        <SelectTrigger className="h-12" data-testid={testid}>
          <SelectValue placeholder={loading ? "Memuat data..." : placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map((o) => (
            <SelectItem key={o.code} value={o.code} data-testid={`${testid}-option-${o.code}`}>
              {o.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {loading && <p className="text-sm text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Mengambil data wilayah...</p>}
    </div>
  );
}

function LogoPreview({ logo, loading, name }) {
  if (!name) return null;
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 bg-slate-50">
      <div className="h-20 w-20 rounded-lg bg-white border grid place-items-center overflow-hidden">
        {loading ? (
          <Loader2 className="animate-spin text-slate-400" />
        ) : logo ? (
          <img src={logo} alt="Logo Kabupaten" className="h-full w-full object-contain p-1" data-testid="setup-regency-logo" />
        ) : (
          <ImageOff className="text-slate-300" />
        )}
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-700">Logo {name}</div>
        <div className="text-xs text-slate-400">{loading ? "Mencari logo dari Wikipedia..." : logo ? "Otomatis dari Wikipedia" : "Logo belum tersedia"}</div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-11" />
    </div>
  );
}
